import { BibExemplarModel } from '@/models/biblioteca/bibexemplar/bibexemplar.model';
import { BibExemplarPesquisaModel } from '@/models/biblioteca/bibexemplar/bibexemplar-pesquisa.model';
import api from '@/services/axios/api';
import { AxiosResponse } from 'axios';
import { DataSource, FiltrosModel } from '@/datasources/consulte-grid';
import TextoValor from '@/models/texto-valor';
import { RegisterOutput } from '@/models/register-output';
import router from '@/routes';
import { useStore } from '@/store';

class BibExemplarService {
  private getRouteSaveContext() {
    const currentRoute = router.currentRoute.value;
    const routePath = currentRoute.path;
    const routeName = String(currentRoute.name || '');
    const isCentral =
      routePath.includes('/cadastroexemplar/0/') ||
      routePath.includes('/cadastroexemplar/0') ||
      routeName === 'bibexemplar_create_central' ||
      routePath.includes('/biblioteca/bibexemplar/cadastro/0/');
    const bibDocumentoId =
      Number(currentRoute.query.bibDocumentoId) ||
      Number(currentRoute.params.bibDocumentoId) ||
      0;

    return { isCentral, bibDocumentoId };
  }

  private normalizeForSave(bibExemplar: BibExemplarModel): BibExemplarModel {
    const { isCentral, bibDocumentoId } = this.getRouteSaveContext();

    return {
      ...bibExemplar,
      bibDocumentoId: Number(bibExemplar.bibDocumentoId) || bibDocumentoId || null,
      gesEscolaId: isCentral ? 0 : useStore().getters.getCodigoEscola,
      bibVolumeId: bibExemplar.bibVolumeId || null,
      bibLocalizacaoId: bibExemplar.bibLocalizacaoId || null,
      bibFormaAquisicaoId: bibExemplar.bibFormaAquisicaoId || null,
      bibExemparAnteriorId: bibExemplar.bibExemparAnteriorId || null,
      bibMotivoBaixaId: bibExemplar.bibMotivoBaixaId || null,
      bibTombamentoId: bibExemplar.bibTombamentoId || null,
      codigoBarra: bibExemplar.codigoBarra || null,
      identificacao: bibExemplar.identificacao || null,
      observacao: bibExemplar.observacao ?? '',
      dataBaixa: bibExemplar.dataBaixa || null,
      baixado: Number(bibExemplar.baixado) === 1 ? 1 : 0,
      podeEmprestar: Number(bibExemplar.podeEmprestar) === 1 ? 1 : 0,
    } as BibExemplarModel;
  }

  dadosIndexPaginado(
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[]
  ): Promise<AxiosResponse<DataSource<BibExemplarModel>>> {
    const routePath = router.currentRoute.value.path;
    const lsItens = routePath.split('/').filter(Boolean);

    const isCentral = lsItens[lsItens.length - 2] === '0';
    const bibDocumentoId = Number(lsItens[lsItens.length - 1]);
    const gesEscolaId = isCentral ? 0 : useStore().getters.getCodigoEscola;

    return api.post<DataSource<BibExemplarModel>>(
      '/BibExemplar/RetornoPaginado',
      {
        rowsPerPage: itensPorPagina,
        pageNumber: pagina,
        filters,
      },
      {
        params: {
          BibDocumentoId: bibDocumentoId,
          GesEscolaId: gesEscolaId,
        },
      }
    );
  }

  inserirAtualizar(
    bibExemplar: BibExemplarModel
  ): Promise<AxiosResponse<RegisterOutput>> {
    const payload = this.normalizeForSave(bibExemplar);

    return api.post<RegisterOutput>('/BibExemplar', payload, {
      params: {
        BibDocumentoId: payload.bibDocumentoId,
        GesEscolaId: payload.gesEscolaId,
      },
    });
  }

  remover(id: number): Promise<AxiosResponse> {
    return api.delete(`/BibExemplar/${id}`);
  }

  atualizar(bibExemplar: BibExemplarModel): Promise<AxiosResponse> {
    return api.put('/BibExemplar', bibExemplar);
  }

  async get(id: number): Promise<BibExemplarModel> {
    const result = await api.get<BibExemplarModel>(`/BibExemplar/${id}`);
    return result.data;
  }
  async GetPorTombamento(identificacao: string): Promise<BibExemplarModel> {
    const result = await api.get<BibExemplarModel>('/BibExemplar/GetPorTombamento', {
      params: { identificacao },
    });
    return result.data;
  }

  /**
   * Busca exemplares da biblioteca por tombamento, código de barras ou título.
   *
   * É o que permite escolher o exemplar em vez de digitar o tombamento de cabeça. A busca é
   * sempre dentro da biblioteca da tela (`gesEscolaId`; 0 é a central) — emprestar exemplar de
   * outra é recusado na gravação, e oferecê-lo aqui só produziria erro.
   */
  async pesquisar(
    termo: string,
    gesEscolaId: number,
    limite = 20
  ): Promise<BibExemplarPesquisaModel[]> {
    const result = await api.get<BibExemplarPesquisaModel[]>('/BibExemplar/Pesquisar', {
      params: { termo, gesEscolaId, limite },
    });

    return Array.isArray(result.data) ? result.data : [];
  }

  async dropDownList(id: number): Promise<TextoValor[]> {
    const endpoint = `/BibExemplar/DropDownList/${id}`;
    const result = await api.get<TextoValor[]>(endpoint);
    return result.data;
  }
}

export default new BibExemplarService();
